import React from 'react';
import { generatePath, useParams } from 'react-router';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import * as RestApi from 'core/network/RestApi';
import { ITemplate } from 'entities/template/template';
import AppLoader from 'core/ui/components/AppLoader';
import { TemplateApiEndpoints } from '../../constants/api-endpoints';
import { RcsTemplateEditorProvider } from './context/RcsTemplateEditorProvider';
import { RcsTemplateEditor } from './RcsTemplateEditor';
import { defaultContextValues } from './context/useRcsTemplateEditorContext';
import { RcsTemplateEditorReducer } from './context/RcsTemplateEditorReducer';
import { templateBodyToRcsConfig } from './utils/rcsConfigToCardContent';
import { Layout, Overlay } from './components/style';

const ErrorOverlay: React.FC<{ message: string }> = ({ message }) => (
  <Overlay>
    <Layout data-testid="RcsTemplateEditorErrorLayout" style={{ alignItems: 'center', justifyContent: 'center' }}>
      <p>{message}</p>
    </Layout>
  </Overlay>
);

/** Page that loads an RCS template by ID and language, then renders the editor with hydrated state. */
export const RcsTemplateEditorPage: React.FC = () => {
  const { t } = useTranslation('designs');
  const { siteId, id: templateId, language } = useParams<{ siteId: string; id: string; language: string }>();

  const templateQuery = useQuery(
    ['template', siteId, templateId, language],
    async () => {
      const result = await RestApi.get<ITemplate>(
        generatePath(TemplateApiEndpoints.GET_TEMPLATE, { siteId, templateId, language }),
      );
      return result.data;
    },
    { enabled: Boolean(siteId && templateId && language) },
  );

  const value = React.useMemo(() => {
    const template = templateQuery.data;
    const templateName = template?.templateName || t('rcsTemplateEditor.defaultName');
    const templateType = template?.type ?? defaultContextValues.templateType;
    const supportedLanguages =
      template?.supportedLanguages?.map(l => ({ language: l.language })) ?? defaultContextValues.supportedLanguages;
    const rcsConfig = templateBodyToRcsConfig(template?.template);
    if (rcsConfig === null) {
      return { loadError: true as const };
    }
    return {
      loadError: false as const,
      initialState: {
        ...defaultContextValues,
        templateName,
        templateType,
        selectedLanguage: language || defaultContextValues.selectedLanguage,
        supportedLanguages,
        rcsConfig,
        lastSavedRcsConfig: rcsConfig,
        enrichmentConfigIds: template?.enrichmentConfigIds ?? defaultContextValues.enrichmentConfigIds,
      },
      reducer: RcsTemplateEditorReducer,
    };
  }, [templateQuery.data, language, t]);

  if (templateQuery.isLoading) {
    return (
      <Overlay data-testid="RcsTemplateEditorLoadingLayout">
        <AppLoader />
      </Overlay>
    );
  }

  if (!siteId || !templateId || !language) {
    return (
      <Overlay data-testid="RcsTemplateEditorLoadingLayout">
        <AppLoader />
      </Overlay>
    );
  }

  if (templateQuery.isError) {
    if (templateQuery.error) {
      // eslint-disable-next-line no-console
      console.error('RCS template load failed:', templateQuery.error);
    }
    return <ErrorOverlay message={t('rcsTemplateEditor.loadError')} />;
  }

  if (!templateQuery.data) {
    return (
      <Overlay data-testid="RcsTemplateEditorLoadingLayout">
        <AppLoader />
      </Overlay>
    );
  }

  if (value.loadError) {
    return <ErrorOverlay message={t('rcsTemplateEditor.loadError')} />;
  }

  return (
    <RcsTemplateEditorProvider
      key={`${siteId}-${templateId}-${language}`}
      value={{ initialState: value.initialState, reducer: value.reducer }}
    >
      <RcsTemplateEditor />
    </RcsTemplateEditorProvider>
  );
};
